// Sound effects in the style of sfxr, made in the browser from a few Web Audio nodes per sound
// (no files): tones and noise with an envelope, a pitch slide, a filter sweep, echoes. Each
// sound is a table entry below, tuned by ear (Wunsch User 2026-10-23); `sound.ts` decides
// whether and how loud they play.

/** One part of a sound. `freq` Hz (noise: the filter's start, if no `filter`), sliding to `to`. */
interface Tone {
  wave: OscillatorType | 'noise';
  freq: number;
  to?: number;
  /** Seconds: after `delay`, up in `attack`, held for `hold`, down in `decay`. */
  delay?: number;
  attack?: number;
  hold?: number;
  decay: number;
  /** Share of the sound's loudness. */
  gain?: number;
  /** A filter whose cut-off sweeps from `from` to `to`. */
  filter?: { type: BiquadFilterType; from: number; to?: number; q?: number };
  /** Pitch wobble: `rate` per second, `depth` Hz. */
  vibrato?: { rate: number; depth: number };
}

interface Sfx {
  tones: Tone[];
  /** Echoes after this many seconds, each this much quieter. */
  echo?: { after: number; times: number; fade: number };
  /** Always the same pitch (the countdown's steps must not wander). */
  exact?: boolean;
}

const shot = (body: number, bodyTo: number, crack: number, decay: number, gain = 1): Tone[] => [
  { wave: 'noise', freq: crack, filter: { type: 'lowpass', from: crack, to: 200 }, decay: decay * 0.8, gain: 0.9 * gain },
  { wave: 'square', freq: body, to: bodyTo, decay, gain: 0.45 * gain },
];
const explosion: Tone[] = [
  { wave: 'noise', freq: 4000, filter: { type: 'lowpass', from: 4000, to: 120 }, attack: 0.005, decay: 0.75, gain: 1 },
  { wave: 'sine', freq: 90, to: 32, decay: 0.55, gain: 0.8 },
];
const notes = (freqs: number[], step: number, last: number, wave: OscillatorType = 'square', gain = 0.5): Tone[] =>
  freqs.map((freq, k) => ({ wave, freq, delay: k * step, hold: k === freqs.length - 1 ? last : step * 0.5, decay: 0.06, gain }));

export const SFX = {
  /** The countdown at 3, 2 and 1: a round low pulse with a little echo, like starting lights;
   * a whole tone higher at each step (`sound.ts`). */
  count: {
    exact: true,
    tones: [
      { wave: 'square', freq: 440, hold: 0.09, decay: 0.12, gain: 0.55 },
      { wave: 'sine', freq: 220, hold: 0.09, decay: 0.15, gain: 0.5 },
    ],
    echo: { after: 0.13, times: 2, fade: 0.35 },
  },
  /** Go: two bright tones rising into a chord, longer, with a shimmer on top. */
  go: {
    tones: [
      { wave: 'square', freq: 660, to: 880, attack: 0.01, hold: 0.35, decay: 0.45, gain: 0.45 },
      { wave: 'square', freq: 990, to: 1320, attack: 0.01, hold: 0.35, decay: 0.45, gain: 0.3 },
      { wave: 'triangle', freq: 1760, delay: 0.05, hold: 0.25, decay: 0.4, gain: 0.25, vibrato: { rate: 9, depth: 18 } },
    ],
    echo: { after: 0.16, times: 2, fade: 0.3 },
  },
  // Shots, by cannon (shop order): Standard, Heavy, Rapid, Precision, Scatter, Drum, Charge, Twin.
  shot0: { tones: shot(190, 60, 3200, 0.13) },
  shot1: { tones: [...shot(110, 34, 1600, 0.3, 1.2), { wave: 'sine', freq: 70, to: 30, decay: 0.3, gain: 0.6 }] },
  shot2: { tones: shot(420, 200, 5200, 0.055, 0.6) },
  shot3: { tones: [{ wave: 'square', freq: 950, to: 300, decay: 0.08, gain: 0.4 }, { wave: 'noise', freq: 7000, filter: { type: 'highpass', from: 2500 }, decay: 0.05, gain: 0.6 }] },
  shot4: { tones: [{ wave: 'noise', freq: 2600, filter: { type: 'lowpass', from: 2600, to: 180 }, decay: 0.2, gain: 1 }, { wave: 'square', freq: 160, to: 55, decay: 0.15, gain: 0.35 }] },
  shot5: { tones: shot(230, 80, 3600, 0.11, 0.85) },
  shot6: { tones: [{ wave: 'sawtooth', freq: 300, to: 1100, decay: 0.16, gain: 0.35 }, ...shot(240, 70, 4000, 0.14)] },
  shot7: { tones: [...shot(200, 70, 3400, 0.09, 0.8), ...shot(205, 72, 3300, 0.09, 0.8).map((t) => ({ ...t, delay: 0.035 }))] },
  /** A bullet bouncing off a wall: a short metallic ping. */
  bounce: { tones: [{ wave: 'square', freq: 1800, to: 1400, decay: 0.06, gain: 0.35 }, { wave: 'triangle', freq: 2700, decay: 0.05, gain: 0.3 }] },
  /** A hit on a tank: a dull clonk. */
  hit: { tones: [{ wave: 'noise', freq: 900, filter: { type: 'lowpass', from: 900, to: 200 }, decay: 0.12, gain: 0.9 }, { wave: 'sine', freq: 150, to: 80, decay: 0.12, gain: 0.7 }] },
  /** Blocked by reactive tiles: a brighter clank. */
  blocked: { tones: [{ wave: 'triangle', freq: 2200, to: 1700, decay: 0.15, gain: 0.45 }, { wave: 'noise', freq: 6000, filter: { type: 'highpass', from: 3500 }, decay: 0.06, gain: 0.5 }] },
  explosion: { tones: explosion },
  /** A tank destroyed: the explosion and a falling tone. */
  destroyed: { tones: [...explosion, { wave: 'square', freq: 420, to: 70, delay: 0.04, decay: 0.55, gain: 0.3 }] },
  /** Two tanks ramming: a low thud and a short scrape. */
  ram: {
    tones: [
      { wave: 'noise', freq: 700, filter: { type: 'lowpass', from: 700, to: 100 }, decay: 0.2, gain: 1 },
      { wave: 'sine', freq: 75, to: 40, decay: 0.22, gain: 0.8 },
      { wave: 'noise', freq: 2200, filter: { type: 'bandpass', from: 2200, to: 1400, q: 4 }, delay: 0.02, decay: 0.09, gain: 0.4 },
    ],
  },
  /** Sinking in water: a filtered splash and bubbles. */
  splash: {
    tones: [
      { wave: 'noise', freq: 900, filter: { type: 'bandpass', from: 900, to: 300, q: 2 }, decay: 0.45, gain: 0.9 },
      { wave: 'sine', freq: 320, to: 140, delay: 0.08, decay: 0.35, gain: 0.35, vibrato: { rate: 18, depth: 60 } },
    ],
  },
  /** Own power-up picked up: three notes up. */
  pickup: { tones: notes([660, 880, 1320], 0.06, 0.06) },
  /** Own power-up used: a whoosh. */
  use: { tones: [{ wave: 'noise', freq: 400, filter: { type: 'bandpass', from: 400, to: 3200, q: 3 }, attack: 0.03, decay: 0.25, gain: 0.8 }] },
  /** A negative power-up hits the own tank: a wobbly motif down, with the falling symbol. */
  afflicted: {
    tones: [
      { wave: 'square', freq: 720, to: 360, decay: 0.16, gain: 0.4, vibrato: { rate: 9, depth: 25 } },
      { wave: 'square', freq: 560, to: 240, delay: 0.14, decay: 0.24, gain: 0.4, vibrato: { rate: 9, depth: 25 } },
    ],
  },
  /** A shockwave going off: a deep whump fading out, with an echo. */
  shockwave: {
    tones: [
      { wave: 'sine', freq: 62, to: 34, attack: 0.01, hold: 0.05, decay: 0.6, gain: 1 },
      { wave: 'noise', freq: 600, filter: { type: 'lowpass', from: 600, to: 80 }, decay: 0.5, gain: 0.6 },
    ],
    echo: { after: 0.15, times: 2, fade: 0.4 },
  },
  /** Hit by a poison bullet: a hiss. */
  poison: {
    tones: [
      { wave: 'noise', freq: 5200, filter: { type: 'bandpass', from: 4200, to: 6500, q: 1.4 }, attack: 0.02, hold: 0.12, decay: 0.35, gain: 0.55 },
      { wave: 'noise', freq: 8000, filter: { type: 'highpass', from: 7000 }, delay: 0.05, decay: 0.25, gain: 0.25 },
    ],
  },
  /** A shield breaking: glass. */
  shieldBreak: {
    tones: [
      { wave: 'triangle', freq: 3000, to: 2300, decay: 0.2, gain: 0.4 },
      { wave: 'noise', freq: 7000, filter: { type: 'highpass', from: 5000 }, decay: 0.16, gain: 0.5 },
      { wave: 'triangle', freq: 4300, delay: 0.03, decay: 0.15, gain: 0.25 },
    ],
  },
  roundWin: { tones: notes([523, 659, 784], 0.09, 0.25) },
  roundLose: { tones: notes([392, 330, 262], 0.12, 0.3, 'triangle', 0.6) },
  matchWin: { tones: [...notes([523, 659, 784, 1047], 0.11, 0.5), ...notes([262, 330, 392, 523], 0.11, 0.5, 'triangle', 0.4)] },
  click: { tones: [{ wave: 'square', freq: 1200, decay: 0.025, gain: 0.25 }] },
  buy: { tones: [{ wave: 'square', freq: 880, to: 1180, decay: 0.07, gain: 0.3 }] },
  done: { tones: notes([660, 990], 0.07, 0.08, 'square', 0.3) },
  /** Daily challenge done: a short fanfare up to a held chord, with a shimmer on top. */
  challenge: {
    tones: [
      ...notes([523, 659, 784], 0.11, 0.11, 'square', 0.35),
      { wave: 'square', freq: 1047, delay: 0.33, hold: 0.45, decay: 0.5, gain: 0.35 },
      { wave: 'square', freq: 784, delay: 0.33, hold: 0.45, decay: 0.5, gain: 0.22 },
      { wave: 'triangle', freq: 2093, delay: 0.38, hold: 0.35, decay: 0.45, gain: 0.2, vibrato: { rate: 10, depth: 25 } },
    ],
  },
} satisfies Record<string, Sfx>;
export type SfxName = keyof typeof SFX;

/** At most this many of one sound per `SAME_WINDOW` seconds, and voices at once. */
const SAME_MAX = 3;
const SAME_WINDOW = 0.06;
const VOICES_MAX = 24;
/** Pitch varies by up to this share each time, so a salvo does not sound like one. */
const VARY = 0.05;

let audio: AudioContext | undefined;
let out: GainNode | undefined;
let noise: AudioBuffer | undefined;
const recent = new Map<SfxName, number[]>();
let voices: number[] = []; // end times

function setup(): AudioContext {
  if (audio) return audio;
  audio = new AudioContext();
  const squash = audio.createDynamicsCompressor(); // salvos must not clip
  squash.threshold.value = -18;
  squash.ratio.value = 6;
  out = audio.createGain();
  out.connect(squash).connect(audio.destination);
  noise = audio.createBuffer(1, audio.sampleRate, audio.sampleRate);
  const data = noise.getChannelData(0);
  for (let k = 0; k < data.length; k++) data[k] = Math.random() * 2 - 1;
  return audio;
}

/** Plays `name` at `volume` (0..1 of full), panned by `pan` (−1 left … 1 right), `pitch` times as high. */
export function playSfx(name: SfxName, volume: number, pan = 0, pitch = 1): void {
  if (volume <= 0) return;
  const ctx = setup();
  if (ctx.state === 'suspended') void ctx.resume(); // browsers start it only after a key or click
  const now = ctx.currentTime;
  const times = (recent.get(name) ?? []).filter((t) => now - t < SAME_WINDOW);
  voices = voices.filter((t) => t > now);
  if (times.length >= SAME_MAX || voices.length >= VOICES_MAX) return;
  times.push(now);
  recent.set(name, times);
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(out!);
  const sfx: Sfx = SFX[name];
  if (!sfx.exact) pitch *= 1 + (Math.random() * 2 - 1) * VARY;
  const echoes = sfx.echo ? sfx.echo.times : 0;
  let end = now;
  for (let e = 0; e <= echoes; e++) {
    const at = now + (sfx.echo ? e * sfx.echo.after : 0);
    const loud = volume * (sfx.echo ? sfx.echo.fade ** e : 1);
    for (const t of sfx.tones) end = Math.max(end, tone(ctx, t, at, loud, pitch, panner));
  }
  voices.push(end);
}

/** One tone from `at`; returns when it ends. */
function tone(ctx: AudioContext, t: Tone, at: number, volume: number, pitch: number, to: AudioNode): number {
  const start = at + (t.delay ?? 0);
  const [attack, hold] = [t.attack ?? 0.005, t.hold ?? 0];
  const stop = start + attack + hold + t.decay;
  const gain = ctx.createGain();
  const peak = volume * (t.gain ?? 1);
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(peak, start + attack);
  gain.gain.setValueAtTime(peak, start + attack + hold);
  gain.gain.exponentialRampToValueAtTime(Math.max(1e-4, peak * 0.001), stop);
  let source: AudioScheduledSourceNode;
  if (t.wave === 'noise') {
    const n = ctx.createBufferSource();
    n.buffer = noise!;
    n.loop = true;
    n.playbackRate.value = pitch;
    source = n;
  } else {
    const o = ctx.createOscillator();
    o.type = t.wave;
    o.frequency.setValueAtTime(t.freq * pitch, start);
    if (t.to) o.frequency.exponentialRampToValueAtTime(t.to * pitch, stop);
    if (t.vibrato) {
      const lfo = ctx.createOscillator();
      const depth = ctx.createGain();
      lfo.frequency.value = t.vibrato.rate;
      depth.gain.value = t.vibrato.depth;
      lfo.connect(depth).connect(o.frequency);
      lfo.start(start);
      lfo.stop(stop);
    }
    source = o;
  }
  let chain: AudioNode = source;
  const f = t.filter ?? (t.wave === 'noise' ? { type: 'lowpass' as BiquadFilterType, from: t.freq } : undefined);
  if (f) {
    const filter = ctx.createBiquadFilter();
    filter.type = f.type;
    filter.Q.value = f.q ?? 0.8;
    filter.frequency.setValueAtTime(f.from * pitch, start);
    if (f.to) filter.frequency.exponentialRampToValueAtTime(f.to * pitch, stop);
    chain.connect(filter);
    chain = filter;
  }
  chain.connect(gain).connect(to);
  source.start(start);
  source.stop(stop + 0.02);
  return stop;
}
